import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentAESTDate } from '../../utils/dateUtils';
import {
  LayoutDashboard,
  Building2,
  Users,
  ClipboardCheck,
  FileText,
  Shield,
  UserCheck,
  Archive,
  Package,
  Clock,
  Stethoscope,
  MapPin,
  Home,
  FileStack,
  Radio,
  CreditCard
} from 'lucide-react';
import { Truck, Wrench } from 'lucide-react';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  external?: boolean;
}

const navigationItems: NavigationItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Overview of key metrics and recent activity across the portal' },
  { name: 'Operations', href: '/operations', icon: Radio, description: 'Live board, timesheets, and out-of-area requests' },
  { name: 'Credit Card Expenses', href: '/credit-card-expenses', icon: CreditCard, description: 'Track and manage company credit card expenditure' },
  { name: 'Sites', href: '/sites-management', icon: Building2, description: 'Manage job sites, waste tracking, and disposal facilities' },
  { name: 'SWMS', href: '/swms', icon: FileText, description: 'Safe Work Method Statements and safety paperwork' },
  { name: 'Workforce', href: '/workforce', icon: Users, description: 'Workers, inductions, and health monitoring records' },
  { name: 'Fleet & Pre-Start', href: '/fleet', icon: Truck, description: 'Vehicles, plant machinery, servicing, and pre-start reports' },
  { name: 'Users', href: '/users', icon: UserCheck, description: 'Manage portal user accounts and permissions' },
  { name: 'Archived Sites', href: '/archived-sites', icon: Archive, description: 'View completed and archived job sites with payment records' },
  { name: 'Privacy Policy', href: '/privacy', icon: Shield, description: 'Data privacy and information handling policy' },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const [healthAlertCount, setHealthAlertCount] = React.useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = React.useState(0);
  const [vehicleOverdueCount, setVehicleOverdueCount] = React.useState(0);
  const [vehicleDueSoonCount, setVehicleDueSoonCount] = React.useState(0);

  React.useEffect(() => {
    checkHealthMonitoringAlerts();
    checkPendingOutOfAreaRequests();
    checkVehicleAlerts();

    // Poll all badges every 60 seconds
    const pollInterval = setInterval(() => {
      checkHealthMonitoringAlerts();
      checkPendingOutOfAreaRequests();
      checkVehicleAlerts();
    }, 60_000);

    // Realtime: out of area requests
    const ooareSub = supabase
      .channel('sidebar_out_of_area')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'out_of_area_requests' }, () => {
        checkPendingOutOfAreaRequests();
      })
      .subscribe();

    // Realtime: health records & worker changes (fit testing N/A toggle etc.)
    const healthSub = supabase
      .channel('sidebar_health')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'health_records' }, () => {
        checkHealthMonitoringAlerts();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workers' }, () => {
        checkHealthMonitoringAlerts();
      })
      .subscribe();

    // Realtime: vehicle & inspection changes
    const vehicleSub = supabase
      .channel('sidebar_vehicles')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles' }, () => {
        checkVehicleAlerts();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_inspections' }, () => {
        checkVehicleAlerts();
      })
      .subscribe();

    return () => {
      clearInterval(pollInterval);
      supabase.removeChannel(ooareSub);
      supabase.removeChannel(healthSub);
      supabase.removeChannel(vehicleSub);
    };
  }, []);

  const checkVehicleAlerts = async () => {
    try {
      const { data: vehiclesData, error: vehiclesError } = await supabase
        .from('vehicles')
        .select('id, rego_expiry_date, rego_na, nhvr_required');

      if (vehiclesError) return;

      const { data: inspectionsData } = await supabase
        .from('vehicle_inspections')
        .select('vehicle_id, inspection_type, expiry_date')
        .order('expiry_date', { ascending: false });

      const today = getCurrentAESTDate();
      let overdueCount = 0;
      let dueSoonCount = 0;

      (vehiclesData || []).forEach(vehicle => {
        // Rego — skip N/A vehicles
        if (!vehicle.rego_na && vehicle.rego_expiry_date) {
          const days = Math.ceil((new Date(vehicle.rego_expiry_date).getTime() - today.getTime()) / 86_400_000);
          if (days < 0) overdueCount++;
          else if (days <= 30) dueSoonCount++;
        }

        // NHVR — only for vehicles that require it
        if (vehicle.nhvr_required === true) {
          const latest = (inspectionsData || [])
            .find(i => i.vehicle_id === vehicle.id && i.inspection_type === 'nhvr');
          if (!latest) {
            overdueCount++;
          } else {
            const days = Math.ceil((new Date(latest.expiry_date).getTime() - today.getTime()) / 86_400_000);
            if (days < 0) overdueCount++;
            else if (days <= 30) dueSoonCount++;
          }
        }
      });

      setVehicleOverdueCount(overdueCount);
      setVehicleDueSoonCount(dueSoonCount);
    } catch (error) {
      console.error('Error checking vehicle alerts:', error);
    }
  };

  const checkPendingOutOfAreaRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('out_of_area_requests')
        .select('id')
        .eq('status', 'pending');

      if (error) {
        console.error('Error fetching pending out of area requests:', error);
        return;
      }

      setPendingRequestsCount(data?.length || 0);
    } catch (error) {
      console.error('Error checking pending out of area requests:', error);
    }
  };
  const checkHealthMonitoringAlerts = async () => {
    try {
      // Get only active workers who require health monitoring - using EXACT same logic as HealthMonitoringPage
      const { data: workersData, error: workersError } = await supabase
        .from('workers')
        .select('*')
        .eq('status', 'active')
        .eq('requires_health_monitoring', true);

      if (workersError) {
        console.error('Error fetching workers for health alerts:', workersError);
        return;
      }

      // Get health records - using EXACT same logic as HealthMonitoringPage
      const { data: healthRecords, error: healthError } = await supabase
        .from('health_records')
        .select('*')
        .order('created_at', { ascending: false });

      if (healthError && healthError.code !== 'PGRST116') {
        console.error('Error fetching health records for alerts:', healthError);
        return;
      }

      // Use EXACT same logic as HealthMonitoringPage tiles
      const today = getCurrentAESTDate();

      // Calculate using EXACT same logic as the tiles in HealthMonitoringPage
      const healthMonitoringOverdueCount = (workersData || []).filter(worker => {
        const latestRecord = (healthRecords || []).find(record => record.worker_id === worker.id);
        return !latestRecord?.health_monitoring_date ||
               (latestRecord.health_monitoring_due_date && new Date(latestRecord.health_monitoring_due_date) < today);
      }).length;

      const fitTestingOverdueCount = (workersData || []).filter(worker => {
        if (worker.fit_testing_not_applicable) return false;
        const latestRecord = (healthRecords || []).find(record => record.worker_id === worker.id);
        return !latestRecord?.fit_testing_date ||
               (latestRecord.fit_testing_due_date && new Date(latestRecord.fit_testing_due_date) < today);
      }).length;

      // Count unique workers who are overdue for EITHER health monitoring OR fit testing
      const workersWithRedTiles = new Set<string>();
      (workersData || []).forEach(worker => {
        const latestRecord = (healthRecords || []).find(record => record.worker_id === worker.id);

        const healthMonitoringOverdue = !latestRecord?.health_monitoring_date ||
                                       (latestRecord.health_monitoring_due_date && new Date(latestRecord.health_monitoring_due_date) < today);

        const fitTestingOverdue = !worker.fit_testing_not_applicable && (
          !latestRecord?.fit_testing_date ||
          (latestRecord.fit_testing_due_date && new Date(latestRecord.fit_testing_due_date) < today)
        );

        if (healthMonitoringOverdue || fitTestingOverdue) {
          workersWithRedTiles.add(worker.id);
        }
      });
      
      // Add the two tile counts together for the sidebar badge
      const alertCount = healthMonitoringOverdueCount + fitTestingOverdueCount;
      setHealthAlertCount(alertCount);
      
      console.log('Health Monitoring Debug:', {
        workersRequiringHealthMonitoring: workersData?.length || 0,
        healthMonitoringOverdueCount,
        fitTestingOverdueCount,
        totalOverdueItems: alertCount,
        shouldMatch: 'Health Monitoring page tile counts',
        note: 'Only counting workers who require health monitoring'
      });
    } catch (error) {
      console.error('Error checking health monitoring alerts:', error);
    }
  };

  return (
    <div className="w-64 bg-white shadow-lg">
      <div className="p-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Shield className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">SafetyPortal</h1>
            <p className="text-xs text-gray-500">Induction Management</p>
          </div>
        </div>
      </div>
      
      <nav className="mt-8">
        <div className="px-3">
          {navigationItems.map((item) => {
            const fleetPaths = ['/fleet', '/vehicles', '/vehicle-plant-servicing', '/pre-start-reports'];
            const workforcePaths = ['/workforce', '/workers', '/inductions', '/health-monitoring'];
            const operationsPaths = ['/operations', '/live-board', '/timesheets', '/out-of-area-requests', '/timesheet-amendments'];
            const swmsPaths = ['/swms', '/safety-paperwork'];
            const sitesPaths = ['/sites-management', '/sites', '/waste-tracking', '/waste-disposal-facilities'];
            const isActive = item.href === '/fleet'
              ? fleetPaths.includes(location.pathname)
              : item.href === '/workforce'
                ? workforcePaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
                : item.href === '/operations'
                  ? operationsPaths.includes(location.pathname)
                  : item.href === '/sites-management'
                    ? sitesPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
                    : item.href === '/swms'
                      ? swmsPaths.includes(location.pathname)
                      : location.pathname === item.href;
            const isHealthMonitoring = item.href === '/workforce';
            const isOutOfAreaRequests = item.href === '/operations';
            const isVehicles = item.href === '/fleet';
            const showAlert = isHealthMonitoring && healthAlertCount > 0;
            const showPendingBadge = isOutOfAreaRequests && pendingRequestsCount > 0;
            const showVehicleOverdue = isVehicles && vehicleOverdueCount > 0;
            const showVehicleDueSoon = isVehicles && vehicleDueSoonCount > 0;

            const className = `relative flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
              isActive
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${(showAlert || showVehicleOverdue) ? 'animate-pulse border-2 border-red-500' : ''}`;

            const style = (showAlert || showVehicleOverdue) ? {
              animation: 'pulse-red-border 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            } : {};

            const content = (
              <>
                {(showAlert || showVehicleOverdue) && (
                  <style jsx>{`
                    @keyframes pulse-red-border {
                      0%, 100% {
                        border-color: rgb(239 68 68); /* red-500 */
                        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
                      }
                      50% {
                        border-color: rgb(220 38 38); /* red-600 */
                        box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3);
                      }
                    }
                  `}</style>
                )}
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
                {showAlert && (
                  <div className="absolute -top-1 -right-1">
                    <div className="h-5 w-5 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <span className="text-xs font-bold text-white">{healthAlertCount > 99 ? '99+' : healthAlertCount}</span>
                    </div>
                  </div>
                )}
                {(showVehicleOverdue || showVehicleDueSoon) && (
                  <div className="absolute -top-1 -right-1 flex space-x-0.5">
                    {showVehicleOverdue && (
                      <div className="h-5 min-w-[20px] px-1 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                        <span className="text-xs font-bold text-white">{vehicleOverdueCount > 99 ? '99+' : vehicleOverdueCount}</span>
                      </div>
                    )}
                    {showVehicleDueSoon && (
                      <div className="h-5 min-w-[20px] px-1 bg-amber-500 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{vehicleDueSoonCount > 99 ? '99+' : vehicleDueSoonCount}</span>
                      </div>
                    )}
                  </div>
                )}
                {showPendingBadge && (
                  <div className="absolute -top-1 -right-1">
                    <div className="h-5 w-5 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}</span>
                    </div>
                  </div>
                )}
              </>
            );

            // Render external links differently
            if (item.external) {
              return (
                <div key={item.name} className="relative group">
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    style={style}
                  >
                    {content}
                  </a>
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 hidden group-hover:block">
                    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-56 whitespace-normal leading-snug shadow-xl">
                      {item.description}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  className={className}
                  style={style}
                >
                  {content}
                </Link>
                <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-56 whitespace-normal leading-snug shadow-xl">
                    {item.description}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
};